// Lender events (ticket 192 D11): one read-only timeline per loan.
// Arive-linked loans: built from the `arive` mirror the 134 feed keeps on the
// loan (stage history, current status, key dates, trackers, lock dates,
// archive). Every date is Arive's own; nothing here is typed in BFY.
// BFY-originated loans: the loan's own status moves (the status milestones).
// Arive sends no conditions in the feed, so there are no condition events.
import { fromApi, dueState } from '../../kit/time';
import { LOAN_STATUS_LABELS } from '../../losApi';

// "APPROVED_WITH_CONDITION" -> "Approved with condition"
export const stageLabel = (s) => {
  const words = String(s || '').toLowerCase().split('_').filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const ACRONYMS = new Set(['hoi']);

// "dateToAvoidEPO" -> "Date to avoid EPO", "initialLESentDate" -> "Initial LE sent date"
export const keyDateLabel = (name) => {
  const words = String(name || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(' ')
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) || (w.length > 1 && w === w.toUpperCase()) ? w.toUpperCase() : w.toLowerCase()));
  const text = words.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const valid = (at) => at != null && !Number.isNaN(fromApi(at).getTime());

export function ariveEvents(arive) {
  if (!arive) return [];
  const out = [];
  const status = arive.status || {};
  const history = arive.stage_history || [];
  // The adverse reason belongs to the current stage. Undated stages sort last in
  // the feed, so the current one is the last entry with its name, not the last entry.
  const currentIdx = status.stage ? history.map((h) => h.stage).lastIndexOf(status.stage) : -1;
  history.forEach((h, i) => {
    const current = i === currentIdx;
    out.push({
      id: `stage-${h.stage}-${i}`,
      kind: 'stage',
      at: h.at || null,
      title: `Stage: ${stageLabel(h.stage)}`,
      detail: current ? status.adverse_reason || null : null,
    });
  });
  // An adverse or suspended status is not always in the stage list.
  if (status.stage && !history.some((h) => h.stage === status.stage)) {
    out.push({
      id: `status-${status.stage}`,
      kind: 'stage',
      at: status.at || null,
      title: `Stage: ${stageLabel(status.stage)}`,
      detail: status.adverse_reason || null,
    });
  }
  Object.entries(arive.key_dates || {}).forEach(([name, at]) => {
    if (!valid(at)) return;
    out.push({ id: `date-${name}`, kind: 'date', at, title: keyDateLabel(name), detail: null });
  });
  (arive.trackers || []).forEach((t) => {
    out.push({
      id: `tracker-${t.name}`,
      kind: 'tracker',
      at: t.date || null,
      title: `${t.label || stageLabel(t.name)} tracker`,
      detail: t.status || null,
    });
  });
  const p = arive.pricing || {};
  if (valid(p.lock_date)) {
    const rate = p.note_rate != null ? `${p.note_rate}%` : null;
    out.push({
      id: 'lock',
      kind: 'lock',
      at: p.lock_date,
      title: 'Rate locked',
      detail: [rate, p.lender_name, p.lock_status].filter(Boolean).join(' · ') || null,
    });
  }
  if (valid(p.lock_expiration_date)) {
    out.push({ id: 'lock-expires', kind: 'lock', at: p.lock_expiration_date, title: 'Rate lock expires', detail: null });
  }
  if (arive.archived) {
    out.push({ id: 'archived', kind: 'archive', at: arive.archived_at || null, title: 'Archived in Arive', detail: null });
  }
  return out;
}

// The loan's own status moves: milestones whose key is a loan status (the
// status PATCH writes one per move; manual milestones may not use these keys).
export function bfyStatusEvents(milestones) {
  return (milestones || [])
    .filter((m) => Object.prototype.hasOwnProperty.call(LOAN_STATUS_LABELS, m.key))
    .map((m) => ({
      id: m.milestone_id,
      kind: 'status',
      at: m.occurred_at || null,
      title: `Status: ${LOAN_STATUS_LABELS[m.key]}`,
      detail: m.notes || null,
    }));
}

// {upcoming, past, undated}. Upcoming = a later calendar day than today (a
// date-only value is its UTC day, a timed one the viewer's local day, as task
// due dates); soonest first. Past = today or earlier, newest first; on a tie
// the later entry in the feed goes first.
export function groupEvents(events, now = new Date()) {
  const t = (e) => fromApi(e.at).getTime();
  const dated = events.map((e, i) => ({ e, i })).filter(({ e }) => valid(e.at));
  const future = ({ e }) => dueState(e.at, now) === null;
  return {
    upcoming: dated.filter(future).sort((a, b) => t(a.e) - t(b.e) || a.i - b.i).map(({ e }) => e),
    past: dated.filter((x) => !future(x)).sort((a, b) => t(b.e) - t(a.e) || b.i - a.i).map(({ e }) => e),
    undated: events.filter((e) => !valid(e.at)),
  };
}
